// O RÓTULO do sync de saúde, decidido por CONTAGEM — não por ausência de erro.
//
// O defeito medido em 20/08 que este módulo mata: quatro conexões
// health_connect com last_sync_status='ok', history_access=true, read_denied
// vazio e as cinco marcas d'água gravadas — e ZERO linhas em health_metrics,
// sleep_logs e workout_sessions. O rótulo saía de
//
//     errors.length === 0 ? "ok" : "partial"
//
// e 'ok' ali significa "não deu erro", não "trouxe alguma coisa". Cinco tipos
// voltando vazios com todas as permissões concedidas carimbava 'ok' E avançava
// as cinco marcas. É a terceira variação do defeito da task 4b852662: antes
// dizia conectado e não trazia; depois trazia e dizia desconectado; agora não
// traz nada e diz ok.
//
// ZERO imports de propósito (disciplina de healthMap/pageRows/chunkIds): a
// classificação tem que ser testável sem device, sem plugin e sem Supabase —
// dentro do hook ela era exatamente o que não dava para verificar.

/** Os cinco tipos que o syncHealth lê, nos nomes das COLUNAS/métricas. */
export interface TypeOutcome {
  type: "sleep" | "workouts" | "steps" | "heart_rate" | "hrv";
  /** false = pulado por permissão (typesToSkip). Não é erro nem vazio. */
  attempted: boolean;
  /** Amostras/buckets que o PLUGIN devolveu — antes de lápide/edição/merge. */
  read: number;
  /**
   * Linhas que o upsert CONFIRMOU — `.select().length`, NUNCA writable.length.
   * A §4.9 existe porque upsert barrado por RLS devolve vazio sem lançar;
   * contar a intenção em vez da confirmação é como este bug nasceu.
   */
  written: number;
  errored: boolean;
}

export type SyncOutcome =
  | "ok"
  | "empty"
  | "partial"
  | "permission_partial"
  | "permission_denied";

/**
 * Classifica a rodada. MUDANÇA CIRÚRGICA: só o ramo de permissão CHEIA
 * (permissionLabel falsy) muda em relação ao critério antigo — ganha o
 * "empty". Os rótulos de permissão passam DIRETO, inalterados.
 *
 * Precedência, nesta ordem:
 *   permissionLabel não-falsy      → devolve permissionLabel   (INALTERADO)
 *   algum outcome.errored          → "partial"
 *   nenhum attempted               → "permission_denied"
 *   todos os attempted com read=0  → "empty"                   (NOVO)
 *   senão                          → "ok"
 *
 * "empty" é "nada em lugar NENHUM": um tipo com dado basta para "ok". Sono
 * com 3 noites e passos zerado é uma pessoa sem contador de passos, não uma
 * conexão doente.
 *
 * ⚠️ DEFEITO CONHECIDO, DE FORA DESTE MÓDULO DE PROPÓSITO: quando
 * permissionLabel é "permission_partial" E um tipo autorizado errou, o erro
 * some — o rótulo de permissão engole o de falha. É OUTRO defeito, com outro
 * raio de impacto (o card já mostra faixa em permission_partial, então nada
 * fica verde), e consertá-lo aqui mudaria o comportamento de TODA conexão
 * parcial junto. Registrado; não resolvido. O teste que pina essa precedência
 * existe para a mudança futura ser deliberada, não acidental.
 */
export function classifySync(
  outcomes: TypeOutcome[],
  permissionLabel: string | null,
): SyncOutcome {
  if (permissionLabel) {
    // O vocabulário de permissão pertence a permissionState.ts; este módulo
    // se recusa a interpretá-lo — passa adiante o que veio. O cast é a
    // fronteira declarada entre os dois vocabulários.
    return permissionLabel as SyncOutcome;
  }
  if (outcomes.some((o) => o.errored)) return "partial";
  const attempted = outcomes.filter((o) => o.attempted);
  if (attempted.length === 0) return "permission_denied";
  if (attempted.every((o) => o.read === 0)) return "empty";
  return "ok";
}

/**
 * A marca d'água de um tipo só avança quando ele foi tentado, não errou E
 * leu ALGUMA COISA. Leitura vazia mantém a marca onde estava.
 *
 * Por que travar em leitura vazia se a marca é inerte hoje: healthMap.ts
 * documenta (healthWindowStart) que `Math.min(fromMark, defaultStart)` escolhe
 * o mais antigo, então com marca fresca a janela é sempre os 30 dias cheios —
 * não avançar não custa NADA agora. O seguro é para o dia em que alguém trocar
 * o `min` por `max` atrás da economia de leitura: nesse dia, uma marca
 * avançada sobre leitura vazia viraria buraco permanente — as quatro conexões
 * de 20/08 tinham as CINCO marcas avançadas sobre zero linhas escritas.
 */
export function shouldAdvanceWatermark(o: TypeOutcome): boolean {
  return o.attempted && !o.errored && o.read > 0;
}
